import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as coreTypes } from '@sitebuilder/core';
import { localization as commonLocalization } from '@sitebuilder/common';
import { MediaApiClient } from '../api/mediaApiClient';
import { ThunkResult } from './index';

export interface StateShape {
    media: coreTypes.Media | null;
    isMediaLoaded: boolean;
    mediaLoading: boolean;
    mediaLoadError: any | null;
    mediaSaveLoading: boolean;
    mediaSaveSucess: string;
    mediaSaveError: any | null;
    medias: [coreTypes.Media] | null;
    mediasLoading: boolean;
    mediasLoadError: any | null;
    selectedMedia: string[];
    versions: any[] | null;
    versionsLoading: boolean;
    versionsLoadError: any | null;
    revertedVersion: boolean;
    revertedVersionError: any | null;
    showDeletedItems: boolean;
    newFolderSaveLoading: boolean;
    newFolderSaveError: any | null;
    newFolderSaveSucess: string;
}

export const mediaDefaultState: StateShape = {
    media: null,
    isMediaLoaded: false,
    mediaLoading: false,
    mediaLoadError: null,
    mediaSaveLoading: false,
    mediaSaveSucess: '',
    mediaSaveError: null,
    medias: null,
    mediasLoading: false,
    mediasLoadError: null,
    selectedMedia: [],
    versions: null,
    versionsLoading: false,
    versionsLoadError: null,
    revertedVersion: false,
    revertedVersionError: null,
    showDeletedItems: false,
    newFolderSaveLoading: false,
    newFolderSaveError: null,
    newFolderSaveSucess: '',
};

const apiClient = new MediaApiClient();

export const actionCreators = {
    loadMedia: (mediaId: string): ThunkResult<Promise<coreTypes.Media | undefined>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(mediaReducerActions.MEDIA_LOAD_START());

            try {
                const media = await apiClient.getMedia(mediaId, {
                    accessToken,
                });
                dispatch(mediaReducerActions.MEDIA_LOAD_END({ media }));

                if (media) {
                    dispatch(mediaReducerActions.SET_IS_MEDIA_LOADED(true));
                }

                return media;
            } catch (err: any) {
                console.error(err);
                dispatch(mediaReducerActions.SET_IS_MEDIA_LOADED(true));
                dispatch(mediaReducerActions.MEDIA_LOAD_END({ error: { message: err.message } }));
            }
        };
    },
    saveNewMedia: (mediaId: string, media: any): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(mediaReducerActions.MEDIA_SAVE_START());

            try {
                await apiClient.saveMedia(mediaId, media, {
                    accessToken,
                });
                dispatch(
                    mediaReducerActions.MEDIA_SAVE_END({
                        success: appI18n.t('media_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    mediaReducerActions.MEDIA_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    saveNewFolder: (folderName: string): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(mediaReducerActions.NEWFOLDER_SAVE_START());

            try {
                await apiClient.saveNewFolder(folderName, {
                    accessToken,
                });
                dispatch(
                    mediaReducerActions.NEWFOLDER_SAVE_END({
                        success: appI18n.t('folder_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    mediaReducerActions.NEWFOLDER_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    listMedias: (
        folderName: string,
        filters: { idPattern?: string[]; authorsIn?: Record<string, unknown> | [] } = {
            idPattern: [],
            authorsIn: [],
        },
        sort?: { field?: string; dir?: string },
        take?: number,
        skip?: number
    ): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(mediaReducerActions.MEDIAS_LOAD_START());

            try {
                const medias = await apiClient.listMedias(
                    folderName,
                    state.media.showDeletedItems,
                    {
                        accessToken,
                    },
                    filters,
                    sort,
                    take,
                    skip
                );
                dispatch(mediaReducerActions.MEDIAS_LOAD_END({ medias }));
            } catch (err: any) {
                console.error(err);
                dispatch(
                    mediaReducerActions.MEDIAS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },
    setSelectedMedia: (selectedMedia: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(mediaReducerActions.SET_SELECTED_MEDIA(selectedMedia));
        };
    },
    unselectMedia: (selectedMedia: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(mediaReducerActions.REMOVE_SELECTED_MEDIA(selectedMedia));
        };
    },
    unselectAllMedia: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(mediaReducerActions.REMOVE_ALL_SELECTED_MEDIA());
        };
    },
    loadMediaVersions: (mediaName: string): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(mediaReducerActions.VERSIONS_LOAD_START());

            try {
                const items = await apiClient.getMediaVersions(mediaName, {
                    accessToken,
                });
                dispatch(
                    mediaReducerActions.VERSIONS_LOAD_END({
                        versions: items,
                    })
                );
            } catch (err: any) {
                dispatch(
                    mediaReducerActions.VERSIONS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    revertVersions: (mediaName: string | null, versionNumber: any) => {
        return async (dispatch: any, getState: any) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;

            try {
                const results = await apiClient.revertVersion(mediaName, versionNumber, {
                    accessToken,
                });
                const revertedItem = await results.json();

                if (!isNaN(revertedItem.VersionNumber)) {
                    dispatch(mediaReducerActions.REVERTED_VERSION({}));
                }
            } catch (err: any) {
                dispatch(
                    mediaReducerActions.REVERTED_VERSION({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    setShowDeletedItems: (): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch(mediaReducerActions.SET_SHOW_DELETED_ITEMS(!state.media.showDeletedItems));
        };
    },
};

const mediaSlice = createSlice({
    name: 'medias',
    initialState: mediaDefaultState,
    reducers: {
        MEDIA_LOAD_START(draft) {
            draft.mediaLoading = true;
            draft.media = null;
            draft.mediaLoadError = null;
            draft.mediaSaveSucess = '';
            draft.newFolderSaveSucess = '';
        },
        MEDIA_LOAD_END(draft, action: PayloadAction<{ media?: coreTypes.Media; error?: any }>) {
            const { media, error } = action.payload;
            draft.mediaLoading = false;
            draft.media = media || null;
            draft.mediaLoadError = error;
        },
        MEDIA_SAVE_START(draft) {
            draft.mediaSaveLoading = true;
            draft.mediaSaveError = null;
        },
        MEDIA_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.mediaSaveLoading = false;
            draft.mediaSaveSucess = success || '';
            draft.mediaSaveError = error;
        },
        MEDIAS_LOAD_START(draft) {
            draft.mediasLoading = true;
            draft.medias = null;
            draft.mediasLoadError = null;
        },
        MEDIAS_LOAD_END(draft, action: PayloadAction<{ medias?: [coreTypes.Media]; error?: any }>) {
            const { medias, error } = action.payload;
            draft.mediasLoading = false;
            draft.medias = medias || null;
            draft.mediasLoadError = error;
            draft.revertedVersion = false;
        },
        NEWFOLDER_SAVE_START(draft) {
            draft.newFolderSaveLoading = true;
            draft.newFolderSaveError = null;
        },
        NEWFOLDER_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.newFolderSaveLoading = false;
            draft.newFolderSaveSucess = success || '';
            draft.newFolderSaveError = error;
        },
        SET_IS_MEDIA_LOADED(draft, action: PayloadAction<boolean>) {
            draft.isMediaLoaded = action.payload;
        },
        SET_SELECTED_MEDIA(draft, action: PayloadAction<string[]>) {
            draft.selectedMedia = [...draft.selectedMedia, ...action.payload];
        },
        REMOVE_SELECTED_MEDIA(draft, action: PayloadAction<string[]>) {
            draft.selectedMedia = draft.selectedMedia.filter(
                (media) => !action.payload.includes(media)
            );
        },
        REMOVE_ALL_SELECTED_MEDIA(draft) {
            draft.selectedMedia = [];
        },
        VERSIONS_LOAD_START(draft) {
            draft.versionsLoading = true;
            draft.versions = null;
            draft.versionsLoadError = null;
        },
        VERSIONS_LOAD_END(draft, action: PayloadAction<{ versions?: any[]; error?: any }>) {
            const { versions, error } = action.payload;
            draft.versionsLoading = false;
            draft.versions = versions || null;
            draft.versionsLoadError = error;
            draft.revertedVersion = false;
        },
        REVERTED_VERSION(draft, action: PayloadAction<{ error?: any }>) {
            draft.revertedVersion = true;
            draft.revertedVersionError = action.payload.error;
        },
        SET_SHOW_DELETED_ITEMS(draft, action: PayloadAction<boolean>) {
            draft.showDeletedItems = action.payload;
        },
    },
});

export const mediaReducerActions: any = mediaSlice.actions;
export const mediaReducer = mediaSlice.reducer;
